import { clsx } from "clsx";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Section } from "@/components/Section";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import type {
  BotConnectInterestOption,
  BotConnectInterestSchema,
  LeadMagnetNextStepsPropsSchema,
  NextStepCard,
  NextStepCardSchema,
  OnboardingChannel,
  OnboardingSchema,
  TelegramConnectSchema,
} from "@/schemas/sections";
import type { PostSubmitContext } from "@/types/config";
import type { SectionWrapperProps } from "@/types/section-wrapper";
import { sanitizePostSubmit } from "@/utils/post-submit-context";
import { readStorage, writeStorage } from "@/utils/safe-storage";
import { trackBotConnectInterestView, trackLeadMagnetNext } from "@/utils/tracking";

export type LeadMagnetNextStepsSectionProps = z.input<typeof LeadMagnetNextStepsPropsSchema> &
  SectionWrapperProps & {
    // SSR-resolved post-submit context (email/source/kind) threaded from
    // +onBeforeRender via pageProps → renderSection, so the correct copy variant
    // is in the initial HTML (no post-hydration flash).
    postSubmit?: PostSubmitContext | null;
  };

const DEFAULT_EMOJI: Record<NextStepCard["kind"], string> = {
  webinar: "🎓",
  explore: "🧭",
  telegram: "✈️",
  custom: "✨",
};

type Tone = NonNullable<NextStepCard["tone"]>;

// Vibrant colorways mirroring the FAQ carousel (FaqCarouselSection) brand
// palette so the upsell cards read as colorful, not flat white.
const TONE_STYLES: Record<
  Tone,
  { card: string; title: string; body: string; chip: string; badge: string; cta: string }
> = {
  purple: {
    card: "bg-brand",
    title: "text-brand-content",
    body: "text-brand-content/80",
    chip: "bg-brand-content/15",
    badge: "bg-brand-content/90 text-brand",
    cta: "text-brand-content",
  },
  peach: {
    card: "bg-(--tone-peach-bg)",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-fg/10",
    badge: "bg-fg/15 text-fg",
    cta: "text-brand",
  },
  mint: {
    card: "bg-(--tone-mint-bg)",
    title: "text-(--tone-mint-ink)",
    body: "text-(--tone-mint-muted)",
    chip: "bg-(--tone-mint-ink)/10",
    badge: "bg-(--tone-mint-ink)/15 text-(--tone-mint-ink)",
    cta: "text-(--tone-mint-ink)",
  },
  coral: {
    card: "bg-(--tone-coral-bg)",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-fg/10",
    badge: "bg-fg/15 text-fg",
    cta: "text-brand",
  },
  blue: {
    card: "bg-(--tone-blue-bg)",
    title: "text-(--tone-blue-ink)",
    body: "text-(--tone-blue-muted)",
    chip: "bg-(--tone-blue-ink)/10",
    badge: "bg-(--tone-blue-ink)/15 text-(--tone-blue-ink)",
    cta: "text-(--tone-blue-ink)",
  },
  lavender: {
    card: "bg-surface-brand",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-brand/12",
    badge: "bg-brand/15 text-fg",
    cta: "text-brand",
  },
};

// Component-family "content-on-color" tone palette — mirrors FaqCarouselSection.tsx
// (this component's sibling and, alphabetically, the canonical definition site)
// verbatim: same custom-property names + values. No shared CSS file the two
// TSX-only components both import exists yet, so each defines its own copy via
// inline `style` on its root wrapper. Hoist to tokens/semantic.css (or a shared
// tone-tokens.css) once a CSS file is in scope for both.
const TONE_PALETTE_VARS = {
  "--tone-peach-bg": "#ffd6a5",
  "--tone-mint-bg": "#a8e6cf",
  "--tone-mint-ink": "#0f3d2a",
  "--tone-mint-muted": "#215a42",
  "--tone-coral-bg": "#ffb4a2",
  "--tone-blue-bg": "#d7efff",
  "--tone-blue-ink": "#17324d",
  "--tone-blue-muted": "#496174",
} as React.CSSProperties;

const DEFAULT_TONE_ORDER: Tone[] = ["purple", "peach", "blue", "mint", "coral", "lavender"];

interface PageContext {
  email: string;
  source: string;
  kind: string;
  // SSR-resolved (token-keyed): the visitor is already bound to the bot. When
  // true the Telegram-connect module is not rendered at all — no re-link CTA.
  tgConnected: boolean;
  connectTokenAvailable: boolean;
}

/** True when the referrer is a same-origin `/webinars/*` page — the fallback
 *  webinar signal when the server didn't set `?k=` (e.g. a legacy DOI-webinar
 *  forward). The authoritative signal is the `?k=` param from
 *  `funnel_objects.metadata.kind`; this only fills the gap. */
function isWebinarReferrer(): boolean {
  try {
    const ref = document.referrer;
    if (!ref) return false;
    const url = new URL(ref, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/webinars/");
  } catch {
    return false;
  }
}

/** Client-side fallback: read + validate the post-submit context from the URL
 *  (used only when the server didn't supply `postSubmit` — soft-nav / prerender).
 *  Applies the same allow-listing as the server via the shared sanitizer, plus
 *  the `/webinars/` referrer webinar upgrade. */
function readPageContext(): PageContext {
  // Client-only fallback (soft-nav / prerender): the URL can't tell us whether
  // the visitor is already connected, so default `tgConnected` to false — the
  // safe default is to SHOW the connect module.
  if (typeof window === "undefined") {
    return {
      email: "",
      source: "",
      kind: "",
      tgConnected: false,
      connectTokenAvailable: false,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const ctx = sanitizePostSubmit({
    email: params.get("email"),
    p: params.get("p"),
    k: params.get("k"),
    t: params.get("t"),
  });
  const base: PageContext = {
    ...ctx,
    tgConnected: false,
    connectTokenAvailable: ctx.connectTokenAvailable === true,
  };
  if (!base.kind && isWebinarReferrer()) return { ...base, kind: "webinar" };
  return base;
}

function contextFromPostSubmit(ps?: PostSubmitContext | null): PageContext {
  return {
    email: ps?.email ?? "",
    source: ps?.source ?? "",
    kind: ps?.kind ?? "",
    tgConnected: ps?.tgConnected === true,
    connectTokenAvailable: ps?.connectTokenAvailable === true,
  };
}

function renderSubtitle(template: string, email: string, fallback?: string): ReactNode {
  if (!email) {
    return fallback ?? template.replace(/\s*\{email\}/g, "").trim();
  }
  if (!template.includes("{email}")) {
    return template;
  }
  const [before, after = ""] = template.split("{email}");
  return (
    <>
      {before}
      <strong className="font-(weight:--fw-bold) text-fg">{email}</strong>
      {after}
    </>
  );
}

function ArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
    >
      <path
        d="M4 10h11M11 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71l-4.14-3.05-1.99 1.93c-.23.23-.42.42-.86.42z" />
    </svg>
  );
}

function CheckBadge() {
  return (
    <Flex
      as="span"
      aria-hidden="true"
      align="center"
      justify="center"
      className="h-9 w-9 shrink-0 rounded-full bg-brand-content/20"
    >
      <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-5 w-5">
        <path
          d="M4 10.5l3.5 3.5L16 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Flex>
  );
}

/** Tween a number to `target` with requestAnimationFrame (easeOutCubic). Starts
 *  at 0 so SSR (0) and hydration match; jumps straight to `target` when the user
 *  prefers reduced motion or `duration <= 0` (restored state). */
function useTween(target: number, duration = 900): number {
  const [value, setValue] = useState(0);
  const valueRef = useRef(0);
  valueRef.current = value;
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || duration <= 0) {
      setValue(target);
      return;
    }
    const from = valueRef.current;
    let startTs = 0;
    const step = (ts: number) => {
      if (!startTs) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - (1 - p) ** 3;
      setValue(from + (target - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);
  return value;
}

const TG_POLL_MS = 3000;
const TG_POLL_FAST_MS = 1500;
// 10 minutes while a tap is unconfirmed — the OAuth device-flow shape ("leave the
// tab, confirm elsewhere, come back"), which is what this flow actually is. The
// old 3-minute cap gave up while a slow GC webhook was still in flight.
const TG_POLL_MAX_MS = 600000;

/** Telegram Web, for the desktop visitor with no Telegram client installed —
 *  otherwise the deep link lands them on a dead protocol handler. */
const TG_WEB_FALLBACK = "https://web.telegram.org";

/** Read the correlation token from the redirect (`?t=`). It is minted on the
 *  opt_in at submit and echoed by the GC bot funnel to `/api/tg-connect`, so the
 *  page can poll `/api/tg-connect/status` for the REAL connection confirmation. */
function readConnectToken(): string {
  if (typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get("t") ?? "";
  } catch {
    return "";
  }
}

async function saveBotConnectClick(provider: "telegram" | BotConnectInterestOption["provider"]) {
  const token = readConnectToken().trim();
  if (!token) return false;
  try {
    const response = await fetch("/api/tg-connect/click", {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ token, provider }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

interface ConnectGate {
  /** The connect is CONFIRMED by the backend (or restored from this tab). */
  connected: boolean;
  /** The user tapped connect; we are waiting on the confirmation. */
  waiting: boolean;
  /** Whether the rest of the page (onboarding, cards) may render. True when the
   *  gate is inactive, the connect is confirmed, or the user tapped "skip". */
  revealed: boolean;
  /** True once the escape-hatch link should be offered. */
  showSkip: boolean;
  skip: () => void;
  markTapped: () => void;
  /** Deep link, personal native-bind hash applied when it has arrived. */
  href: string;
  restored: boolean;
}

/** Owns the connect state for the whole section, because it GATES the rest of
 *  the page — the connect block alone can't hold it. Polls
 *  `/api/tg-connect/status` for the REAL confirmation (never optimistic on tap).
 *
 *  Gate semantics: the page starts CLOSED whenever an enabled connect block is
 *  configured, so the server-rendered HTML is already the single-CTA page and
 *  nothing flashes into view and back out. It opens when the backend confirms,
 *  when the user says they already connected, or when there is no `?t=` token to
 *  verify against (a direct visit — never leave that visitor at a dead end). */
function useConnectGate(cfg: z.input<typeof TelegramConnectSchema> | undefined): ConnectGate {
  const active = !!cfg && cfg.enabled !== false;
  const [token, setToken] = useState("");
  const [connected, setConnected] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  // No token means no way to verify — open the gate rather than trap the visitor.
  const [unverifiable, setUnverifiable] = useState(false);
  // A confirmed connect in this tab → show 100% straight away on return.
  const [restored, setRestored] = useState(false);
  // The user's personal native-bind hash (extracted async on submit; ~1-2s).
  const [nativeHash, setNativeHash] = useState("");
  const nativeHashRef = useRef("");
  nativeHashRef.current = nativeHash;

  useEffect(() => {
    if (!active) return;
    const tok = readConnectToken();
    setToken(tok);
    if (!tok) {
      setUnverifiable(true);
      return;
    }
    if (readStorage("session", `lc_tg_ok:${tok}`) === "1") {
      setConnected(true);
      setRestored(true);
    }
  }, [active]);

  // Offer the escape hatch only after the user has actually tapped connect and
  // the confirmation has had time to land — so it never competes with the CTA.
  useEffect(() => {
    if (!waiting || connected) return;
    const delay = Math.max(0, cfg?.skipAfterSeconds ?? 25) * 1000;
    const timer = window.setTimeout(() => setShowSkip(true), delay);
    return () => window.clearTimeout(timer);
  }, [waiting, connected, cfg?.skipAfterSeconds]);

  // One poll drives both: the personal native-bind hash for the deep link, and —
  // after the user taps — the GC-side connect confirmation that advances the bar
  // to 100%. Runs from mount to fetch the hash; keeps going while confirming a
  // tap. Re-polls on tab refocus (return from the Telegram app).
  useEffect(() => {
    if (!token || connected) return;
    let stop = false;
    const startedAt = Date.now();
    const poll = async () => {
      if (stop) return;
      try {
        const res = await fetch(`/api/tg-connect/status?t=${encodeURIComponent(token)}`, {
          headers: { accept: "application/json" },
        });
        if (res.ok) {
          const data = (await res.json()) as { connected?: boolean; nativeHash?: string | null };
          if (data?.nativeHash) setNativeHash(data.nativeHash);
          if (data?.connected) {
            setConnected(true);
            writeStorage("session", `lc_tg_ok:${token}`, "1");
            return;
          }
        }
      } catch {}
      // Keep polling while confirming a tap, or until the native hash arrives.
      const needMore =
        !stop && Date.now() - startedAt < TG_POLL_MAX_MS && (waiting || !nativeHashRef.current);
      if (needMore) window.setTimeout(poll, waiting ? TG_POLL_MS : TG_POLL_FAST_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    poll();
    return () => {
      stop = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, connected, waiting]);

  const base = cfg?.href ?? "https://t.me/lenacareer_bot";
  // Prefer the personal native-bind hash (binds Telegram to the EXACT GC user);
  // fall back to the bare bot (still connects, just not natively bound). The hash
  // is RAW on purpose — encoding/markdown must not touch the `__sN`.
  const href = nativeHash ? `${base}${base.includes("?") ? "&" : "?"}start=${nativeHash}` : base;

  return {
    connected,
    waiting,
    revealed: !active || connected || skipped || unverifiable,
    showSkip: showSkip && !connected,
    skip: () => setSkipped(true),
    markTapped: () => setWaiting(true),
    href,
    restored,
  };
}

/** The prominent connect module (the dreamjob-weekend «остался последний шаг /
 *  50%» pattern) — presentational; `useConnectGate` owns the state. */
function TelegramConnectBlock({
  cfg,
  gate,
  botConnectInterest,
  onInterestSelected,
  compact = false,
}: {
  cfg: z.input<typeof TelegramConnectSchema>;
  gate: ConnectGate;
  botConnectInterest?: z.input<typeof BotConnectInterestSchema>;
  onInterestSelected: (option: BotConnectInterestOption) => void;
  compact?: boolean;
}) {
  const { connected, waiting, revealed, restored, href } = gate;
  const start = cfg.progressStart ?? 50;
  const end = cfg.progressEnd ?? 100;
  // The bar tracks the GATE, not just the confirmation: whenever the rest of the
  // page is unlocked the step is done as far as the visitor is concerned, so a
  // 50% bar above revealed content would read as a broken promise.
  const target = revealed ? end : start;
  const progress = useTween(target, restored ? 0 : 900);

  // `revealed` (not `connected`) drives the eyebrow for the same reason as the
  // bar. The check badge and «Готово! Проверьте Telegram» below stay tied to
  // `connected` — those assert a fact about Telegram we may not actually have.
  const eyebrow = revealed
    ? "Готово"
    : waiting
      ? "Ожидаем подключение…"
      : (cfg.eyebrow ?? "Остался последний шаг");

  return (
    <div
      data-component="TelegramConnectBlock"
      className={clsx(
        "mx-auto max-w-xl rounded-3xl bg-brand text-brand-content shadow-lg md:mt-8 md:p-8",
        compact ? "mt-4 p-4" : "mt-6 p-6",
      )}
    >
      <Flex align="center" justify="between" gap={8} className="mb-2">
        <Text variant="label" as="p" className="text-brand-content/80">
          {eyebrow}
        </Text>
        <span className="text-(length:--type-heading) font-(weight:--fw-bold) tabular-nums">
          {Math.round(progress)}%
        </span>
      </Flex>
      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-brand-content/20"
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Прогресс регистрации"
      >
        <div className="h-full rounded-full bg-brand-content" style={{ width: `${progress}%` }} />
      </div>

      {connected ? (
        <Flex align="start" gap={8} className="mt-4">
          <CheckBadge />
          <div className="min-w-0 flex-1">
            <Text variant="subheading" as="p" className="text-brand-content">
              {cfg.connectedTitle ?? "Готово! Проверьте Telegram"}
            </Text>
            {cfg.connectedDescription && (
              <Text variant="body-sm" as="p" className="mt-1 text-brand-content/80">
                {cfg.connectedDescription}
              </Text>
            )}
          </div>
        </Flex>
      ) : (
        <div className="mt-4">
          <Text variant="subheading" as="h2" className="text-brand-content">
            {cfg.title}
          </Text>
          <Text variant="body-sm" as="p" className="mt-1 text-brand-content/85">
            {cfg.description}
          </Text>
          {waiting && (
            <Text variant="body-sm" as="p" className="mt-2 text-brand-content/70">
              Открыли Telegram? Нажмите «Старт» — как подключитесь, отметим здесь автоматически.
            </Text>
          )}
        </div>
      )}

      <Stack gap={8} className="mt-5">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            gate.markTapped();
            void saveBotConnectClick("telegram");
          }}
          data-track="lm_next.cta.telegram_connect"
          className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-brand-content px-6 py-3.5 font-(weight:--fw-bold) text-brand no-underline shadow-md transition-transform duration-300 hover:-translate-y-0.5"
        >
          <TelegramIcon />
          {connected ? "Открыть Telegram" : (cfg.cta ?? "Подключить бота")}
        </a>
        <BotConnectInterestOptions cfg={botConnectInterest} onSelected={onInterestSelected} />
      </Stack>
      {cfg.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-brand-content/70">
          {cfg.note}
        </Text>
      )}
      {!connected && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-brand-content/70">
          Нет Telegram на этом устройстве?{" "}
          <a
            href={TG_WEB_FALLBACK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={gate.markTapped}
            data-track="lm_next.link.telegram_web"
            className="font-(weight:--fw-medium) text-brand-content underline"
          >
            Откройте его в браузере
          </a>
        </Text>
      )}
      {gate.showSkip && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={gate.skip}
            data-track="lm_next.cta.connect_skip"
            className="text-(length:--type-body-sm) font-(weight:--fw-medium) text-brand-content/70 underline"
          >
            {cfg.skipLabel ?? "Я подключил(а) — показать остальное"}
          </button>
        </div>
      )}
    </div>
  );
}

function BotConnectInterestOptions({
  cfg,
  onSelected,
}: {
  cfg?: z.input<typeof BotConnectInterestSchema>;
  onSelected: (option: BotConnectInterestOption) => void;
}) {
  const [savingProvider, setSavingProvider] = useState<BotConnectInterestOption["provider"] | null>(
    null,
  );
  const [saveError, setSaveError] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewTracked = useRef(false);
  const options = cfg?.options ?? [];
  const hasOptions = options.length > 0;

  useEffect(() => {
    if (!hasOptions) return;
    const element = rootRef.current;
    if (!element || viewTracked.current) return;
    const markViewed = () => {
      if (viewTracked.current) return;
      viewTracked.current = true;
      trackBotConnectInterestView();
    };
    if (typeof IntersectionObserver === "undefined") {
      markViewed();
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
          markViewed();
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasOptions]);

  if (!hasOptions) return null;

  const saveInterest = async (option: BotConnectInterestOption) => {
    setSavingProvider(option.provider);
    setSaveError(false);
    try {
      const saved = await saveBotConnectClick(option.provider);
      if (!saved) throw new Error("connect click was not saved");
      onSelected(option);
    } catch {
      setSaveError(true);
    } finally {
      setSavingProvider(null);
    }
  };

  return (
    <div ref={rootRef} data-component="BotConnectInterestOptions" className="grid gap-2">
      {options.map((option) => (
        <Flex
          as="button"
          key={option.provider}
          type="button"
          align="center"
          justify="center"
          gap={8}
          disabled={savingProvider !== null}
          aria-busy={savingProvider === option.provider}
          onClick={() => void saveInterest(option)}
          data-track={`lm_next.cta.${option.provider}_connect_interest`}
          className="min-h-12 w-full cursor-pointer rounded-2xl border-0 bg-brand-content px-6 py-3.5 text-(length:--type-body) font-(weight:--fw-bold) text-brand shadow-md transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70"
        >
          <span
            aria-hidden="true"
            className="rounded-lg bg-brand/10 px-1.5 py-1 text-(length:--type-caption) font-(weight:--fw-heavy)"
          >
            {option.provider === "vk" ? "VK" : "MAX"}
          </span>
          <span>{savingProvider === option.provider ? "Сохраняем выбор…" : option.label}</span>
        </Flex>
      ))}
      {saveError && (
        <div role="alert" className="rounded-2xl bg-brand-content/10 p-3 text-center">
          <Text variant="body-sm" as="p" className="text-brand-content">
            {cfg?.saveError ?? "Не получилось сохранить выбор. Попробуйте ещё раз."}
          </Text>
        </div>
      )}
    </div>
  );
}

BotConnectInterestOptions.displayName = "BotConnectInterestOptions";

function BotConnectInterestConfirmation({ option }: { option: BotConnectInterestOption }) {
  return (
    <Flex
      role="status"
      aria-live="polite"
      align="start"
      gap={12}
      data-component="BotConnectInterestConfirmation"
      className="mx-auto mt-6 max-w-xl rounded-3xl border border-brand/20 bg-surface-brand p-6 text-left md:mt-8"
    >
      <Flex
        as="span"
        aria-hidden="true"
        align="center"
        justify="center"
        className="h-9 w-9 shrink-0 rounded-full bg-brand/10 font-(weight:--fw-heavy) text-brand"
      >
        ✓
      </Flex>
      <div className="min-w-0 flex-1">
        <Text variant="label" as="p" className="text-fg">
          {option.comingSoonTitle}
        </Text>
        <Text variant="body-sm" as="p" className="mt-1 text-muted">
          {option.comingSoonDescription}
        </Text>
      </div>
    </Flex>
  );
}

BotConnectInterestConfirmation.displayName = "BotConnectInterestConfirmation";

function isBotConnectInterestEnabled(
  connectEnabled: boolean,
  connectTokenAvailable: boolean,
  cfg: z.input<typeof BotConnectInterestSchema> | undefined,
): boolean {
  return (
    connectEnabled &&
    connectTokenAvailable &&
    !!cfg &&
    cfg.enabled !== false &&
    (cfg.options?.length ?? 0) > 0
  );
}

/** One upsell card — a full-tap link. Horizontal compact row on mobile, vibrant
 *  vertical card on desktop. Tone falls back to the default order by position. */
function NextStepCardLink({
  card,
  index,
}: {
  card: z.input<typeof NextStepCardSchema>;
  index: number;
}) {
  const external = card.external ?? /^https?:\/\//.test(card.href);
  const tone = TONE_STYLES[card.tone ?? DEFAULT_TONE_ORDER[index % DEFAULT_TONE_ORDER.length]];
  return (
    <a
      href={card.href}
      data-track={`lm_next.cta.${card.kind}`}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={clsx(
        "group flex flex-row items-center gap-3 rounded-2xl p-4 text-left no-underline shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl md:flex-col md:items-stretch md:gap-0 md:rounded-3xl md:p-6 md:hover:-translate-y-1",
        tone.card,
      )}
    >
      <Flex
        as="span"
        aria-hidden="true"
        align="center"
        justify="center"
        className={clsx(
          "h-11 w-11 shrink-0 rounded-xl text-(length:--type-heading) md:mb-3",
          tone.chip,
        )}
      >
        {card.emoji ?? DEFAULT_EMOJI[card.kind]}
      </Flex>
      <div className="min-w-0 flex-1">
        <Flex align="center" justify="between" gap={8}>
          <p className={clsx("font-(weight:--fw-bold)", tone.title)}>{card.title}</p>
          {card.badge && (
            <span
              className={clsx(
                "hidden shrink-0 rounded-full px-2.5 py-1 text-(length:--type-caption) font-(weight:--fw-bold) md:inline-block",
                tone.badge,
              )}
            >
              {card.badge}
            </span>
          )}
        </Flex>
        <p
          className={clsx(
            "mt-0.5 text-(length:--type-body-sm) leading-(--lh-body) md:mt-1 md:leading-(--lh-relaxed)",
            tone.body,
          )}
        >
          {card.description}
        </p>
        <span
          className={clsx(
            "mt-4 hidden items-center gap-2 font-(weight:--fw-bold) md:inline-flex",
            tone.cta,
          )}
        >
          {card.cta}
          <ArrowIcon />
        </span>
      </div>
      <span className={clsx("shrink-0 md:hidden", tone.cta)}>
        <ArrowIcon />
      </span>
    </a>
  );
}

const CHANNEL_EMOJI: Record<OnboardingChannel["id"], string> = {
  "tg-channel": "📣",
  vk: "🅥",
  youtube: "▶️",
  dzen: "🟡",
  custom: "✨",
};

/** The post-connect step: a short list of Elena's owned channels, one tap each.
 *  Deliberately compact — the bot's own welcome sequence is the primary
 *  onboarding, this is the "somewhere to go next" while the user is still here.
 *  Keep it to 3-5 channels; past that the choice itself costs follows. */
function OnboardingStep({ cfg }: { cfg: z.input<typeof OnboardingSchema> }) {
  const channels = cfg.channels ?? [];
  if (channels.length === 0) return null;
  return (
    <div data-component="OnboardingStep" className="mx-auto mt-6 max-w-xl md:mt-8">
      <div className="mb-4 text-center">
        {cfg.eyebrow && (
          <Text variant="label" as="p" className="text-muted">
            {cfg.eyebrow}
          </Text>
        )}
        <Text variant="subheading" as="h2" className="mt-1 text-fg">
          {cfg.title}
        </Text>
        {cfg.subtitle && (
          <Text variant="body-sm" as="p" className="mx-auto mt-1 max-w-lg text-muted">
            {cfg.subtitle}
          </Text>
        )}
      </div>
      <Stack gap={8}>
        {channels.map((channel) => (
          <Flex
            as="a"
            key={channel.href}
            href={channel.href}
            target="_blank"
            rel="noopener noreferrer"
            direction="row"
            align="center"
            gap={12}
            data-track={`lm_next.cta.onboarding_${channel.id}`}
            className="group rounded-2xl border border-border bg-bg p-4 text-left no-underline transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
          >
            <Flex
              as="span"
              aria-hidden="true"
              align="center"
              justify="center"
              className="h-11 w-11 shrink-0 rounded-xl bg-brand/10 text-(length:--type-heading)"
            >
              {channel.emoji ?? CHANNEL_EMOJI[channel.id]}
            </Flex>
            <div className="min-w-0 flex-1">
              <p className="font-(weight:--fw-bold) text-fg">{channel.title}</p>
              <p className="mt-0.5 text-(length:--type-body-sm) leading-(--lh-body) text-muted">
                {channel.description}
              </p>
            </div>
            <Flex
              as="span"
              align="center"
              gap={4}
              className="shrink-0 font-(weight:--fw-bold) text-brand"
            >
              <span className="hidden md:inline">{channel.cta}</span>
              <ArrowIcon />
            </Flex>
          </Flex>
        ))}
      </Stack>
      {cfg.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-muted">
          {cfg.note}
        </Text>
      )}
    </div>
  );
}

interface ResolvedContent {
  title: string;
  subtitle: string;
  subtitleNoEmail?: string;
  spamTitle: string;
  spamNote: string;
  steps: string[];
  nextTitle: string;
  nextSubtitle?: string;
  telegramConnect?: z.input<typeof TelegramConnectSchema>;
  botConnectInterest?: z.input<typeof BotConnectInterestSchema>;
  onboarding?: z.input<typeof OnboardingSchema>;
  cards: z.input<typeof NextStepCardSchema>[];
}

/** Layer content: defaults ← byKind[kind] ← bySource[source] (most specific wins). */
function resolveContent(
  defaults: ResolvedContent,
  kindOv: Partial<ResolvedContent> | undefined,
  sourceOv: Partial<ResolvedContent> | undefined,
): ResolvedContent {
  return {
    title: sourceOv?.title ?? kindOv?.title ?? defaults.title,
    subtitle: sourceOv?.subtitle ?? kindOv?.subtitle ?? defaults.subtitle,
    subtitleNoEmail:
      sourceOv?.subtitleNoEmail ?? kindOv?.subtitleNoEmail ?? defaults.subtitleNoEmail,
    spamTitle: sourceOv?.spamTitle ?? kindOv?.spamTitle ?? defaults.spamTitle,
    spamNote: sourceOv?.spamNote ?? kindOv?.spamNote ?? defaults.spamNote,
    steps: sourceOv?.steps ?? kindOv?.steps ?? defaults.steps,
    nextTitle: sourceOv?.nextTitle ?? kindOv?.nextTitle ?? defaults.nextTitle,
    nextSubtitle: sourceOv?.nextSubtitle ?? kindOv?.nextSubtitle ?? defaults.nextSubtitle,
    telegramConnect:
      sourceOv?.telegramConnect ?? kindOv?.telegramConnect ?? defaults.telegramConnect,
    botConnectInterest:
      sourceOv?.botConnectInterest ?? kindOv?.botConnectInterest ?? defaults.botConnectInterest,
    onboarding: sourceOv?.onboarding ?? kindOv?.onboarding ?? defaults.onboarding,
    cards: sourceOv?.cards ?? kindOv?.cards ?? defaults.cards,
  };
}

/** Resolve the page context: server-provided `postSubmit` (SSR + hydration, no
 *  flash), falling back to the client URL only when absent, plus the `/webinars/`
 *  referrer webinar upgrade. Fires the landing tracking event once on mount. */
function usePostSubmitContext(
  postSubmit: PostSubmitContext | null | undefined,
  mode: string,
): PageContext {
  const [ctx, setCtx] = useState<PageContext>(() => contextFromPostSubmit(postSubmit));
  useEffect(() => {
    const server = contextFromPostSubmit(postSubmit);
    const hasServer = server.email || server.source || server.kind || server.connectTokenAvailable;
    let next = hasServer ? server : readPageContext();
    if (!next.kind && isWebinarReferrer()) next = { ...next, kind: "webinar" };
    setCtx(next);
    trackLeadMagnetNext(next.source, mode);
  }, [mode, postSubmit]);
  return ctx;
}

export function LeadMagnetNextSteps({
  mode = "delivered",
  title,
  subtitle,
  subtitleNoEmail,
  spamTitle = "Письмо не пришло за пару минут?",
  spamNote,
  steps = [],
  supportEmail = "info@lenacareer.ru",
  nextTitle = "Что дальше?",
  nextSubtitle,
  telegramConnect,
  botConnectInterest,
  onboarding,
  cards = [],
  byKind,
  bySource,
  postSubmit,
  id,
  background = "base-100",
  padding = "lg",
  className,
}: LeadMagnetNextStepsSectionProps) {
  const ctx = usePostSubmitContext(postSubmit, mode);
  const {
    title: rTitle,
    subtitle: rSubtitle,
    subtitleNoEmail: rSubtitleNoEmail,
    spamTitle: rSpamTitle,
    spamNote: rSpamNote,
    steps: rSteps,
    nextTitle: resolvedNextTitle,
    nextSubtitle: resolvedNextSubtitle,
    telegramConnect: resolvedTelegramConnect,
    botConnectInterest: resolvedBotConnectInterest,
    onboarding: resolvedOnboarding,
    cards: resolvedCards,
  } = resolveContent(
    {
      title,
      subtitle,
      subtitleNoEmail,
      spamTitle,
      spamNote,
      steps,
      nextTitle,
      nextSubtitle,
      telegramConnect,
      botConnectInterest,
      onboarding,
      cards,
    },
    ctx.kind ? byKind?.[ctx.kind] : undefined,
    ctx.source ? bySource?.[ctx.source] : undefined,
  );

  // An already-connected visitor (`tgConnected`, resolved SSR-side from the `?t=`
  // token) never sees the connect module — no re-link CTA. With the block off,
  // `useConnectGate(undefined)` is inactive → `revealed` is true → the rest of
  // the page (onboarding + upsell cards) shows straight away.
  const connectEnabled =
    !!resolvedTelegramConnect && resolvedTelegramConnect.enabled !== false && !ctx.tgConnected;
  const botConnectInterestEnabled = isBotConnectInterestEnabled(
    connectEnabled,
    ctx.connectTokenAvailable,
    resolvedBotConnectInterest,
  );
  const gate = useConnectGate(connectEnabled ? resolvedTelegramConnect : undefined);
  const [selectedBotConnectInterest, setSelectedBotConnectInterest] =
    useState<BotConnectInterestOption | null>(null);
  const showOnboarding =
    gate.revealed && !!resolvedOnboarding && resolvedOnboarding.enabled !== false;

  const selectBotConnectInterest = (option: BotConnectInterestOption) => {
    setSelectedBotConnectInterest(option);
    gate.skip();
  };

  return (
    <Section
      id={id}
      background={background}
      padding={padding}
      // While the gate is closed the section fills the viewport, so the footer
      // sits below the fold and the connect choices stay the primary action.
      // `svh` (not `vh`) — mobile browser chrome makes `vh` overshoot and push
      // the CTA itself out of view on exactly our 75%-mobile traffic.
      // `grid`+`content-center` rather than flex: ADR-0094 routes flex layout
      // through the Flex primitive, and Section's `containerLayout` only reaches
      // the inner container — the min-height has to live on the section itself.
      className={clsx(!gate.revealed && "grid min-h-svh content-center", className)}
      data-lm-source={ctx.source || undefined}
    >
      <div
        data-component="LeadMagnetNextSteps"
        className="mx-auto w-full max-w-3xl"
        style={TONE_PALETTE_VARS}
      >
        <div className="text-center">
          <div
            data-component="LeadMagnetSuccessIcon"
            className={clsx(botConnectInterestEnabled && "hidden md:block")}
          >
            <Flex
              aria-hidden="true"
              align="center"
              justify="center"
              className="mx-auto mb-3 h-12 w-12 rounded-full bg-brand/10 text-(length:--type-heading) md:mb-4 md:h-14 md:w-14 md:text-3xl"
            >
              {mode === "confirm" ? "✉️" : "🎉"}
            </Flex>
          </div>
          <Text variant="subheading" as="h1" className="text-fg">
            {rTitle}
          </Text>
          <Text variant="body-sm" as="p" className="mx-auto mt-2 max-w-xl text-muted">
            {renderSubtitle(rSubtitle, ctx.email, rSubtitleNoEmail)}
          </Text>
        </div>

        {connectEnabled && resolvedTelegramConnect && !selectedBotConnectInterest && (
          <TelegramConnectBlock
            cfg={resolvedTelegramConnect}
            gate={gate}
            botConnectInterest={botConnectInterestEnabled ? resolvedBotConnectInterest : undefined}
            onInterestSelected={selectBotConnectInterest}
            compact={botConnectInterestEnabled}
          />
        )}

        {selectedBotConnectInterest && (
          <BotConnectInterestConfirmation option={selectedBotConnectInterest} />
        )}

        {showOnboarding && resolvedOnboarding && <OnboardingStep cfg={resolvedOnboarding} />}

        {gate.revealed && resolvedCards.length > 0 && (
          <div className="mt-6 md:mt-8">
            <div className="mb-4 text-center">
              <Text variant="subheading" as="h2" className="text-fg">
                {resolvedNextTitle}
              </Text>
              {resolvedNextSubtitle && (
                <Text variant="body-sm" as="p" className="mx-auto mt-1 max-w-lg text-muted">
                  {resolvedNextSubtitle}
                </Text>
              )}
            </div>
            <div
              className={clsx(
                "grid gap-3",
                resolvedCards.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2",
              )}
            >
              {resolvedCards.map((card, index) => (
                <NextStepCardLink key={`${card.kind}:${card.href}`} card={card} index={index} />
              ))}
            </div>
          </div>
        )}

        <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-border bg-bg p-4 text-left md:mt-8 md:p-6">
          <Text variant="label" as="p" className="flex items-center gap-2 text-fg">
            <span aria-hidden="true">📩</span>
            {rSpamTitle}
          </Text>
          {rSteps.length > 0 && (
            <Stack as="ol" gap={8} className="mt-3">
              {rSteps.map((step, index) => (
                <Flex as="li" key={step} align="start" gap={8}>
                  <Flex
                    as="span"
                    align="center"
                    justify="center"
                    className="mt-px h-5 w-5 shrink-0 rounded-full bg-brand text-(length:--type-caption) font-(weight:--fw-bold) text-brand-content"
                  >
                    {index + 1}
                  </Flex>
                  <Text variant="body-sm" as="span" className="flex-1 text-muted">
                    {step}
                  </Text>
                </Flex>
              ))}
            </Stack>
          )}
          <Text variant="body-sm" as="p" className="mt-3 text-muted">
            {rSpamNote} Не нашли письмо?{" "}
            <a
              href={`mailto:${supportEmail}`}
              data-track="lm_next.link.support"
              className="ds-link ds-link-primary font-(weight:--fw-medium)"
            >
              {supportEmail}
            </a>
          </Text>
        </div>
      </div>
    </Section>
  );
}

LeadMagnetNextSteps.displayName = "LeadMagnetNextSteps";
